import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { Combobox } from '../../components/ui/Combobox';
import { PageHeader } from '../../components/layout/PageHeader';
import { listAccounts } from '../accounts/api';
import type { Account } from '../../types/account';
import type { Rollout } from '../../types/rollout';
import { listRollouts } from './api';

function statusBadge(status: Rollout['status']): string {
  switch (status) {
    case 'draft':
      return 'bg-slate-100 text-slate-700';
    case 'active':
      return 'bg-emerald-100 text-emerald-800';
    case 'archived':
      return 'bg-slate-200 text-slate-500';
  }
}

// Global rollouts list. Per-brand list lives as a section on AccountDetail.
// Supports a brand filter; the "+ New rollout" button is disabled until a
// brand is selected or the user navigates from a brand Account page.
export function RolloutList() {
  const [brandFilter, setBrandFilter] = useState<string>('');

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const brands = useMemo<Account[]>(
    () => (accounts.data ?? []).filter((a) => a.accountType === 'brand'),
    [accounts.data],
  );

  const rollouts = useQuery({
    queryKey: ['rollouts', brandFilter || 'all'],
    queryFn: () =>
      listRollouts(brandFilter ? { brandAccountId: brandFilter } : {}),
  });

  if (rollouts.isLoading || accounts.isLoading) return <CenteredSpinner />;
  if (rollouts.error) {
    return (
      <div className="text-red-600 text-sm">
        Failed to load rollouts: {(rollouts.error as Error).message}
      </div>
    );
  }

  const brandById = new Map(brands.map((b) => [b.id, b]));
  const list = rollouts.data ?? [];

  return (
    <div>
      <PageHeader
        title="Rollouts"
        description="Every active and archived rollout, across every brand."
        actions={
          <>
            <Combobox
              aria-label="Filter by brand"
              searchPlaceholder="Search brands…"
              value={brandFilter}
              onValueChange={setBrandFilter}
              options={[
                { value: '', label: 'All brands' },
                ...brands.map((b) => ({ value: b.id, label: b.name })),
              ]}
            />
            {brandFilter && (
              <Button asChild>
                <Link to={`/rollouts/new?brand=${encodeURIComponent(brandFilter)}`}>
                  New rollout
                </Link>
              </Button>
            )}
          </>
        }
      />

      {list.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          {brandFilter
            ? 'No rollouts for this brand yet. Click "New rollout" to start one.'
            : 'No rollouts yet. Pick a brand to start one.'}
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Brand</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Dates</th>
                <th className="px-4 py-3 font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => {
                const brand = brandById.get(r.brandAccountId);
                const start = r.defaultStartDate ? new Date(r.defaultStartDate).toLocaleDateString() : '—';
                const end = r.defaultEndDate ? new Date(r.defaultEndDate).toLocaleDateString() : '—';
                return (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <Link to={`/rollouts/${r.id}`} className="font-medium text-slate-900 hover:underline">
                        {r.name}
                      </Link>
                      <span className="ml-2 text-xs text-slate-500 font-mono">{r.slug}</span>
                    </td>
                    <td className="px-4 py-2 text-slate-700">{brand?.name ?? r.brandAccountId}</td>
                    <td className="px-4 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusBadge(r.status)}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-slate-700">
                      {start} → {end}
                    </td>
                    <td className="px-4 py-2 text-slate-500">
                      {new Date(r.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
